import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Plus, Trash2, Loader2 } from 'lucide-react';
import { billingApi, Coupon } from '../../lib/api/billing';

export const DiscountsPage: React.FC = () => {
    const navigate = useNavigate();
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadCoupons();
    }, []);

    const loadCoupons = async () => {
        try {
            setLoading(true);
            const data = await billingApi.getCoupons();
            setCoupons(data);
        } catch (error) {
            console.error('Failed to load coupons', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this coupon? It cannot be undone.')) return;
        try {
            await billingApi.deleteCoupon(id);
            loadCoupons();
        } catch (error) {
            alert('Failed to delete coupon');
        }
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm relative">

                {/* Toolbar */}
                <div className="flex items-center justify-between p-2 border-b border-neutral-200 bg-neutral-50/50">
                    <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-2">Discounts (Coupons)</span>
                    <button
                        onClick={() => navigate('/payments/discounts/new')}
                        className="btn-primary !px-3 !py-1.5 !text-xs flex items-center gap-1.5"
                    >
                        <Plus size={12} /> New Coupon
                    </button>
                </div>

                {/* Header Grid */}
                <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    <div className="col-span-3">Code</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-3">Value</div>
                    <div className="col-span-3">Duration</div>
                    <div className="col-span-1 text-right"></div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-neutral-400" /></div>
                    ) : coupons.length === 0 ? (
                        <div className="p-12 text-center text-neutral-500 text-sm">No coupons found. Create one to get started.</div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {coupons.map(coupon => (
                                <div key={coupon.id} onClick={() => navigate('/payments/discounts/' + coupon.id)} className="grid grid-cols-12 gap-4 px-4 py-2 items-center hover:bg-neutral-50/80 transition-colors text-sm group cursor-pointer">
                                    <div className="col-span-3 font-medium text-neutral-900 flex items-center gap-2">
                                        <Tag size={14} className="text-neutral-400" />
                                        {coupon.code}
                                    </div>
                                    <div className="col-span-2" onClick={e => e.stopPropagation()}>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={coupon.isActive}
                                                onChange={async (e) => {
                                                    try {
                                                        const newValue = e.target.checked;
                                                        // Optimistic update
                                                        setCoupons(prev => prev.map(c => c.id === coupon.id ? { ...c, isActive: newValue } : c));
                                                        await billingApi.updateCoupon(coupon.id, { isActive: newValue });
                                                    } catch (err) {
                                                        console.error('Failed to update coupon status', err);
                                                        // Revert
                                                        setCoupons(prev => prev.map(c => c.id === coupon.id ? { ...c, isActive: !coupon.isActive } : c));
                                                    }
                                                }}
                                            />
                                            <div className="w-9 h-5 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                                            <span className="ml-2 text-xs font-medium text-neutral-600">{coupon.isActive ? 'Active' : 'Archived'}</span>
                                        </label>
                                    </div>
                                    <div className="col-span-3 text-neutral-600">
                                        {coupon.percentOff ? `${coupon.percentOff}% off` : `$${(coupon.amountOff || 0) / 100} off`}
                                    </div>
                                    <div className="col-span-3 text-neutral-500 text-xs">
                                        {coupon.duration}
                                        {coupon.duration === 'repeating' && ` (${coupon.durationInMonths} mo)`}
                                    </div>
                                    <div className="col-span-1 text-right flex justify-end">
                                        <button
                                            onClick={(e) => handleDelete(e, coupon.id)}
                                            className="p-1.5 text-neutral-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                            title="Delete (Archive)"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
